import type { Rect } from '../types'

export const MIN_REGION_SIZE = 8

export function normalizeRect(rect: Rect): Rect {
  const x = rect.width < 0 ? rect.x + rect.width : rect.x
  const y = rect.height < 0 ? rect.y + rect.height : rect.y
  return { x, y, width: Math.abs(rect.width), height: Math.abs(rect.height) }
}

export function clampRect(rect: Rect, width: number, height: number): Rect {
  const n = normalizeRect(rect)
  const x = Math.max(0, Math.min(n.x, width))
  const y = Math.max(0, Math.min(n.y, height))
  const right = Math.max(x, Math.min(n.x + n.width, width))
  const bottom = Math.max(y, Math.min(n.y + n.height, height))
  return { x, y, width: right - x, height: bottom - y }
}

export function expandRect(rect: Rect, padding: number, width: number, height: number): Rect {
  return clampRect(
    {
      x: rect.x - padding,
      y: rect.y - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    },
    width,
    height,
  )
}

export function rectContains(rect: Rect, x: number, y: number): boolean {
  return x >= rect.x && y >= rect.y && x <= rect.x + rect.width && y <= rect.y + rect.height
}

export function rectIoU(a: Rect, b: Rect): number {
  const left = Math.max(a.x, b.x)
  const top = Math.max(a.y, b.y)
  const right = Math.min(a.x + a.width, b.x + b.width)
  const bottom = Math.min(a.y + a.height, b.y + b.height)
  const intersection = Math.max(0, right - left) * Math.max(0, bottom - top)
  if (intersection === 0) return 0
  const union = a.width * a.height + b.width * b.height - intersection
  return union <= 0 ? 0 : intersection / union
}

export function dedupeRects<T extends { rect: Rect; confidence: number }>(items: T[], threshold = 0.55): T[] {
  const sorted = [...items].sort((a, b) => b.confidence - a.confidence)
  const result: T[] = []
  for (const item of sorted) {
    if (!result.some((kept) => rectIoU(kept.rect, item.rect) >= threshold)) result.push(item)
  }
  return result
}

export function unionRects(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null
  const left = Math.min(...rects.map((r) => r.x))
  const top = Math.min(...rects.map((r) => r.y))
  const right = Math.max(...rects.map((r) => r.x + r.width))
  const bottom = Math.max(...rects.map((r) => r.y + r.height))
  return { x: left, y: top, width: right - left, height: bottom - top }
}

export function isUsableRect(rect: Rect): boolean {
  return Number.isFinite(rect.x) && Number.isFinite(rect.y) && Number.isFinite(rect.width) && Number.isFinite(rect.height)
    && rect.width >= MIN_REGION_SIZE && rect.height >= MIN_REGION_SIZE
}
